
import Component from 'vue-class-component'
import Vue from 'vue'
import {Prop} from 'vue-property-decorator'
import {User as Storeuser} from '@/store/index'
import DatePicker from '@/components/Atomic/DatePicker.vue'
import OrgForm from './OrgForm.vue'
import CardTitle from '../Atomic/CardTitle.vue'
import GreenButton from '../Atomic/GreenButton.vue'
import RedButton from '../Atomic/RedButton.vue'
import {Extra} from '../../models/user'
import {Organisation} from '../../models/organisation'
import OrganisationsService from '../../services/OrganisationsService'
import ErrorHandler from '@/services/ErrorHandlerService'
import LicenceForm from '@/components/Extras/LicenceForm.vue'
import JobTitlesForm from '@/components/Extras/JobTitlesForm.vue'
import {State} from 'vuex-class'
import ExtrasService from '@/services/ExtrasService'
import Source from '@/models/constants/Source'

@Component({
  components: {
    CardTitle,
    JobTitlesForm,
    DatePicker,
    GreenButton,
    RedButton,
    OrgForm,
    LicenceForm,
  },
})
export default class AccountForm extends Vue {
  public $refs!: {
    datePickerMenu: DatePicker,
  }
  @State public readonly user: Storeuser
  @Prop() public readonly validateText!: string
  @Prop(Function) public readonly cancel!: () => void
  @Prop(Function) public readonly validate!: () => void
  @Prop() public pending!: boolean
  @Prop() private account!: Extra
  @Prop() private extras: Extra[]

  private originalEmail = ''
  private originalIUP = ''
  private originalBirthDate = ''

  private menu: boolean = false
  private title: string[] = ['M', 'F']
  private stepNumber: number = 1
  private validatedOrgs: Organisation[] = []
  private jobTitles: string[] = []
  private orgLicence: string = ''
  private picker: string = ''
  private licencesChoices: string[] = ['eo-0', 'eo-3', 'eo-4', 'eo-5']
  private errorHandler = new ErrorHandler()

  private lastCheckedEmail: string = ''
  private lastCheckedIUP: string = ''
  private lastCheckedBirthDate: string = ''

  private iupExists: boolean = false
  private emailExists: boolean = false
  private birthDateExists: boolean = false

  private isIUPValid: boolean = false
  private isEmailValid: boolean = false

  private isValidatingEmail: boolean = false
  private isValidatingIUP: boolean = false
  private isValidatingBirthDate: boolean = false

  private linkAccountDialog: boolean = false
  private accountToLink: Extra | undefined = undefined

  private mounted() {
    this.$validator.extend('orgValidation', {
      getMessage: (field) => 'The organisation' + field + 'does not exist',
      validate: (value) => !!value,
    })
    this.originalEmail =
        this.account.courriel !== undefined ? this.account.courriel : ''
    this.originalIUP = this.account.iup !== undefined ? this.account.iup : ''
    this.originalBirthDate =
        this.account.date_naissance !== undefined
            ? this.account.date_naissance
            : ''
  }

  private hasNames() {
    return (
        this.account.nom !== '' &&
        this.account.nom !== undefined &&
        this.account.prenom !== '' &&
        this.account.prenom !== undefined
    )
  }

  private get isValidating() {
    return (
        this.pending ||
        this.isValidatingEmail ||
        this.isValidatingIUP ||
        this.isValidatingBirthDate
    )
  }

  private get hasValueConflicts() {
    return this.emailExists || this.iupExists || this.birthDateExists
  }

  private hasRequiredFieldsFilledNoErrors() {
    return (
        !this.isValidating &&
        !this.hasValueConflicts &&
        this.hasNames() &&
        this.account.genre !== '' &&
        this.account.courriel !== '' &&
        this.account.courriel !== undefined &&
        this.$validator.errors.all().length === 0
    )
  }

  private get existingAccountIcon() {
    const icon = 'mdi-account'
    const append =
        this.isIUPValid && !this.isValidatingIUP && this.account.iup !== ''
            ? this.iupExists
                ? '-alert'
                : '-check'
            : ''
    return icon + append
  }

  private get existingAccountIconColor() {
    return this.isIUPValid && !this.isValidatingIUP && this.account.iup !== ''
        ? this.iupExists
            ? 'error'
            : 'success'
        : ''
  }

  private get existingEmailIcon() {
    const icon = 'mdi-email'
    const append =
        this.isEmailValid &&
        !this.isValidatingEmail &&
        this.account.courriel !== '' &&
        this.account.courriel !== undefined
            ? this.emailExists
                ? '-alert'
                : '-check'
            : ''
    return icon + append
  }

  private get existingEmailIconColor() {
    return this.isEmailValid &&
    !this.isValidatingEmail &&
    this.account.courriel !== '' &&
    this.account.courriel !== undefined
        ? this.emailExists
            ? 'error'
            : 'success'
        : ''
  }

  private get formattedDate() {
    return this.account.date_naissance === undefined ||
    this.account.date_naissance === ''
        ? ''
        : (this as any).$moment(this.account.date_naissance).format('L')
  }

  private updateDate() {
    this.$emit('input', this.account.date_naissance)
    this.menu = false
  }

  private async checkExistingMail() {
    const email = this.account.courriel
    const emailValid = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(
        this.account.courriel,
    )
    if (emailValid && !email.endsWith("@edu-vd.ch")) {
      this.isEmailValid = emailValid
      if (email !== this.lastCheckedEmail && email !== this.originalEmail) {
        const MailErrorField = this.$t('SecondaryEmail').toString()
        this.isValidatingEmail = true
        this.lastCheckedEmail = email
        try {
          this.accountToLink = await ExtrasService.checkForExistingMail(
              this.account.courriel,
          )
          if (this.canAccountBeLinked(this.accountToLink!)) {
            this.linkAccountDialog = true
          } else {
            this.emailExists = true
            const emailErrorMessage = this.$t(
                'TEMPACCOUNT.CreateTempAccountEmailAlreadyTaken',
            ).toString()
            this.$validator.errors.add({
              field: MailErrorField,
              msg: emailErrorMessage,
              rule: 'unique',
            })
          }
        } catch (error: any) {
          this.$validator.errors.remove(MailErrorField)
          this.emailExists = false
        } finally {
          this.isValidatingEmail = false
        }
      }
    }
    else {
      this.isEmailValid = false
    }
  }

  private async checkExistingIUP() {
    const iup = this.account.iup
    this.isIUPValid = /^[a-z0-9]{6}$/.test(this.account.iup)
    if (this.isIUPValid) {
      if (iup !== this.lastCheckedIUP && iup !== this.originalIUP) {
        this.isValidatingIUP = true
        this.lastCheckedIUP = iup
        const IUPErrorField = this.$t('IUP').toString()
        try {
          this.accountToLink = await ExtrasService.checkForExistingIUP(
              this.account.iup,
          )
          if (this.canAccountBeLinked(this.accountToLink!)) {
            this.linkAccountDialog = true
          } else {
            this.iupExists = true
            const IUPErrorMessage = this.$t('EXTRAS.IUPAlreadyInUse').toString()
            this.$validator.errors.add({
              field: IUPErrorField,
              msg: IUPErrorMessage,
              rule: 'unique',
            })
          }
        } catch (error: any) {
          this.$validator.errors.remove(IUPErrorField)
          this.iupExists = false
        } finally {
          this.isValidatingIUP = false
        }
      }
    }
  }

  private async checkExistingBirthDate() {
    const birthDate = this.account.date_naissance
    if (
        birthDate &&
        birthDate !== this.lastCheckedBirthDate &&
        this.originalBirthDate !== birthDate && this.originalBirthDate !== ''
    ) {
      this.lastCheckedBirthDate = birthDate

      this.isValidatingBirthDate = true
      const birthDateErrorField = this.$t('BirthDate').toString()
      try {
        this.accountToLink = await ExtrasService.checkForExistingBirthDate(
            this.account.date_naissance,
            this.account.prenom,
            this.account.nom,
        )
        if (this.canAccountBeLinked(this.accountToLink!)) {
          this.linkAccountDialog = true
        } else {
          this.birthDateExists = true
          const birthDateErrorMessage = this.$t(
              'EXTRAS.BirthDateInUse',
          ).toString()
          this.$validator.errors.add({
            field: birthDateErrorField,
            msg: birthDateErrorMessage,
            rule: 'unique',
          })
        }
      } catch (error: any) {
        this.$validator.errors.remove(birthDateErrorField)
        this.birthDateExists = false
      } finally {
        this.isValidatingBirthDate = false
      }
    }
  }

  private canAccountBeLinked(account: Extra) {
    switch (account.source) {
      case Source.STAFF:
      case Source.SUBSTITUTE:
      case Source.TEACHER:
      case Source.REFEDU:
        return this.extras.every((e) => e.personne_ia_id !== account.personne_id && e.iup !== account.iup && e.courriel !== account.courriel)
      default:
        return false
    }
  }

  private get selectedSchoolsName() {
    const selectedSchoolsName: string[] = []
    this.account.school_ids.forEach((schoolId) => {
      const foundSchool = this.user.schools.find(
          (school) => school.etablissement_id === schoolId,
      )
      if (foundSchool != null) {
        selectedSchoolsName.push(foundSchool.nom)
      }
    })
    return selectedSchoolsName.join(', ')
  }

  private async next() {
    const validated =
        this.stepNumber === 1
            ? await this.$validator.validateAll()
            : this.stepNumber === 2
                ? await this.$validator.validateAll('organisation')
                : this.stepNumber === 4
                    ? await this.$validator.validateAll('jobtitle')
                    : true
    if (validated) {
      this.stepNumber++
      if (this.stepNumber === 1) {
        this.account.date_naissance = this.account.date_naissance
      }
      if (this.stepNumber === 2) {
        await this.fetchValidatedOrgs()
      }
      if (this.stepNumber === 3) {
        await this.getOrgJobTitles()
      }
    }
  }

  private async fetchValidatedOrgs() {
    this.$emit('pendingStatusChanged', true)
    try {
      this.validatedOrgs =
          await OrganisationsService.fetchValidatedOrganisations()
    } catch (error: any) {
      this.errorHandler.handle(this, error.response.data.code, true)
    }
    this.$emit('pendingStatusChanged', false)
  }

  private validateOrganisation() {
    return this.validatedOrgs.find((org) => {
      if (
          this.account.department === '' ||
          this.account.department === undefined
      ) {
        this.account.department = null
      }
      if (this.account.entity === '' || this.account.entity === undefined) {
        this.account.entity = null
      }
      if (this.account.unit === '' || this.account.unit === undefined) {
        this.account.unit = null
      }
      if (
          this.account.authority === org.authority &&
          this.account.department === org.department &&
          this.account.entity === org.entity &&
          this.account.unit === org.unit
      ) {
        if (this.account.licence === '' || this.account.licence === undefined && this.orgLicence !== '') {
          this.account.licence = this.orgLicence
        }
        this.orgLicence = org.licence
        if (this.account.licence === '' || this.account.licence === undefined && this.orgLicence !== '') {
          this.account.licence = this.orgLicence
        }
        return true
      }
      return false
    })
  }

  private async getOrgJobTitles() {
    const singleOrg = this.validateOrganisation()
    if (singleOrg) {
      this.jobTitles = singleOrg.job_titles
      this.orgLicence = singleOrg.licence
    } else {
      this.jobTitles = []
      this.orgLicence = ''
    }
  }

  private closeLinkDialog() {
    this.accountToLink = undefined
    this.emailExists = true
    this.linkAccountDialog = false
    this.lastCheckedEmail = ''
    this.$validator.errors.add({
      field: this.$t('SecondaryEmail').toString(),
      msg: this.$t(
          'TEMPACCOUNT.CreateTempAccountEmailAlreadyTaken',
      ).toString(),
      rule: 'unique',
    })
  }

  private linkAccount() {
    this.$set(this.account, 'genre', this.accountToLink!.genre)
    this.account.prenom = this.accountToLink!.prenom
    this.account.nom = this.accountToLink!.nom

    this.lastCheckedEmail = this.accountToLink!.courriel_autre
    this.account.courriel = this.accountToLink!.courriel_autre
    this.lastCheckedIUP = this.accountToLink!.iup
    this.account.iup = this.accountToLink!.iup
    this.lastCheckedBirthDate = this.accountToLink!.date_naissance!
    if (this.accountToLink?.date_naissance) {
      this.account.date_naissance = this.accountToLink.date_naissance
      this.$refs.datePickerMenu.picker = this.account.date_naissance
      this.updateDate()
    }
    this.account.tel_mobile = this.accountToLink!.tel_mobile.replace(/\s/g, "")
    this.account.tel_prive = this.accountToLink!.tel_prive.replace(/\s/g, "")
    this.account.tel_prof = this.accountToLink!.tel_prof.replace(/\s/g, "")
    this.linkAccountDialog = false
    this.account.personne_ia_id = this.accountToLink!.personne_id
    this.emailExists = false
  }
}
